import {
  Session,
  SupabaseClient,
  User as SupabaseUser,
} from '@supabase/supabase-js';

import { AuthSession, User } from '../../../types';
import {
  NoOpSessionAdapter,
  SessionStorageAdapter,
} from '../types/sessionAdapter';

/**
 * Core session management without platform-specific code
 */
export class SessionManager {
  private supabase: SupabaseClient;
  private sessionAdapter: SessionStorageAdapter;

  constructor(
    supabaseClient: SupabaseClient,
    sessionAdapter?: SessionStorageAdapter
  ) {
    this.supabase = supabaseClient;
    this.sessionAdapter = sessionAdapter || new NoOpSessionAdapter();
  }

  // ============================================================================
  // User Transformation
  // ============================================================================

  transformSupabaseUser(supabaseUser: SupabaseUser): User {
    const provider = supabaseUser.app_metadata?.provider || 'email';
    return {
      id: supabaseUser.id,
      email: supabaseUser.email || '',
      provider: provider as 'google' | 'apple' | 'email',
      providerId: supabaseUser.app_metadata?.provider_id || supabaseUser.id,
      createdAt: supabaseUser.created_at || new Date().toISOString(),
      updatedAt: supabaseUser.updated_at || new Date().toISOString(),
    };
  }

  transformSession(session: Session): AuthSession {
    return {
      user: this.transformSupabaseUser(session.user),
      accessToken: session.access_token,
      refreshToken: session.refresh_token || '',
      expiresAt: session.expires_at || 0,
    };
  }

  // ============================================================================
  // Session Management
  // ============================================================================

  async getCurrentSession(): Promise<AuthSession | null> {
    try {
      // Try to get from platform-specific storage first
      if (this.sessionAdapter.isAvailable()) {
        const storedSession = await this.sessionAdapter.getSession();
        if (storedSession) {
          return storedSession;
        }
      }

      // Fall back to Supabase's session management
      const {
        data: { session },
        error,
      } = await this.supabase.auth.getSession();

      if (error || !session) {
        return null;
      }

      const transformedSession = this.transformSession(session);

      // Store in platform-specific storage
      if (this.sessionAdapter.isAvailable()) {
        await this.sessionAdapter.storeSession(transformedSession);
      }

      return transformedSession;
    } catch {
      return null;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const {
        data: { user },
        error: _error,
      } = await this.supabase.auth.getUser();

      if (_error || !user) {
        return null;
      }

      return this.transformSupabaseUser(user);
    } catch {
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const session = await this.getCurrentSession();
    return !!session;
  }

  onSessionChange(
    callback: (session: AuthSession | null, event?: string) => void
  ): () => void {
    const {
      data: { subscription },
    } = this.supabase.auth.onAuthStateChange(
      async (event: string, session: Session | null) => {
        if (session?.user) {
          const transformedSession = this.transformSession(session);

          // Store session in platform-specific storage
          if (this.sessionAdapter.isAvailable()) {
            await this.sessionAdapter.storeSession(transformedSession);
          }

          callback(transformedSession, event);
        } else {
          // Clear session from platform-specific storage
          if (this.sessionAdapter.isAvailable()) {
            await this.sessionAdapter.clearSession();
          }

          callback(null, event);
        }
      }
    );

    return () => subscription.unsubscribe();
  }

  async refreshSession(): Promise<{
    success: boolean;
    session?: AuthSession;
    error?: { code: string; message: string };
  }> {
    try {
      const {
        data: { session },
        error: _error,
      } = await this.supabase.auth.refreshSession();

      if (_error || !session) {
        return {
          success: false,
          error: {
            code: 'REFRESH_ERROR',
            message: _error?.message || 'Failed to refresh session',
          },
        };
      }

      const transformedSession = this.transformSession(session);

      // Store refreshed session
      if (this.sessionAdapter.isAvailable()) {
        await this.sessionAdapter.storeSession(transformedSession);
      }

      return {
        success: true,
        session: transformedSession,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async signOut(): Promise<{
    success: boolean;
    error?: { code: string; message: string };
  }> {
    try {
      const { error } = await this.supabase.auth.signOut();

      // Clear session from platform-specific storage
      if (this.sessionAdapter.isAvailable()) {
        await this.sessionAdapter.clearSession();
      }

      if (error) {
        return {
          success: false,
          error: {
            code: 'SIGNOUT_ERROR',
            message: error.message,
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }
}
